import React, {useCallback, useEffect, useMemo, useRef} from 'react';
import PropTypes from 'prop-types';
import JsonFormsWrapper from '@/components/common/jsonForms/jsonFormsWrapper/JsonFormsWrapper';
import {Dropdown, Toggle} from '@/components/atoms';
import {
  surveyActivityJsonSchema,
  surveyActivityUiSchema,
  surveyAvailabilityJsonSchema,
  surveyAvailabilityUiSchema
} from '@/services/schema/activity';
import {displayOutOfStockSchema} from '@/containers/Rewards/RewardAttributes/schemas';
import rewardSetupStyles from '@/containers/Rewards/RewardSetup/RewardSetup.module.scss';
import styles from './SurveyActivitySetUp.module.scss';

/** Same Toggle uiSchema as Reward Attributes "Display Out of Stock" (`ToggleRow.jsx`). Yes/No comes from `Toggle` → pepsico-ds `value` when checked/unchecked. */
const displayFirstToggleUiSchema = {
  ...displayOutOfStockSchema,
  label: 'Display First'
};

const segmentFieldLabelClass =
  rewardSetupStyles.rewardSetup__segments__toggleLabel;

const DISPLAY_ORDER_OPTIONS = [0, 1, 2, 3, 4, 5].map((n) => ({
  id: String(n),
  displayText: String(n)
}));

const DISPLAY_ORDER_REQUIRED_MSG =
  'Display Order is required when Display First is No. Select a value from 0 to 5.';

const normalizeDisplayOrder = (v) => {
  if (v === '' || v === undefined || v === null) return '';
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 && n <= 5 ? n : '';
};

const SurveyActivitySetUp = ({
  activitySelection,
  formData,
  handleFormChange
}) => {
  /** Baseline order for edits (captured once when activity has id/uuid); new activities restore to '' */
  const editBaselineDisplayOrderRef = useRef(undefined);

  useEffect(() => {
    if (!(formData?.uuid || formData?.id)) return;
    if (editBaselineDisplayOrderRef.current !== undefined) return;
    const v = formData?.displayOrder;
    if (v === undefined || v === null || v === '') return;
    editBaselineDisplayOrderRef.current = normalizeDisplayOrder(v);
  }, [formData?.uuid, formData?.id, formData?.displayOrder]);

  const displayOrderDropdownValue = useMemo(() => {
    const n = normalizeDisplayOrder(formData?.displayOrder);
    if (n === '') return [];
    return [{id: String(n), displayText: String(n)}];
  }, [formData?.displayOrder]);

  const displayFirstOn = !!formData?.displayFirst;

  const displayOrderMissing =
    !displayFirstOn &&
    (formData?.displayOrder === '' ||
      formData?.displayOrder === null ||
      formData?.displayOrder === undefined);

  const handleJsonChange = useCallback(
    (data) => {
      handleFormChange(data);
    },
    [handleFormChange]
  );

  const onDisplayOrderSelect = useCallback(
    (selected) => {
      const item = Array.isArray(selected) ? selected[0] : selected;
      const raw = item?.id ?? item?.displayText ?? item;
      if (raw === '' || raw === undefined || raw === null) {
        handleFormChange({displayOrder: '', displayOrderValid: false});
        return;
      }
      const n = parseInt(String(raw), 10);
      if (!Number.isFinite(n) || n < 0 || n > 5) return;
      handleFormChange({displayOrder: n, displayOrderValid: false});
    },
    [handleFormChange]
  );

  const onDisplayFirstUpdate = useCallback(
    (v) => {
      if (v) {
        const restore =
          editBaselineDisplayOrderRef.current !== undefined
            ? editBaselineDisplayOrderRef.current
            : '';
        handleFormChange({
          displayFirst: true,
          displayOrder: restore === '' ? '' : restore,
          displayOrderValid: false
        });
      } else {
        const order =
          editBaselineDisplayOrderRef.current !== undefined
            ? editBaselineDisplayOrderRef.current
            : 0;
        handleFormChange({
          displayFirst: false,
          displayOrder: order,
          displayOrderValid: false
        });
      }
    },
    [handleFormChange]
  );

  const availabilityUi = useMemo(
    () =>
      surveyAvailabilityUiSchema(
        formData?.startDate || '',
        formData?.endDate,
        formData
      ),
    [formData]
  );

  return (
    <div
      className={`flex w-full flex-col gap-8 rounded-lg px-6 py-4 ${rewardSetupStyles.rewardSetup__card}`}
      data-testid='survey-activity-card'>
      <div className='flex flex-col gap-4'>
        <h3 className='text-base font-bold text-white'>Activity Setup</h3>
        <div className={styles.surveySetupRow}>
          <div className={styles.activityTypeWrap}>{activitySelection}</div>
          <div className={styles.surveyFieldsWrap}>
            <JsonFormsWrapper
              jsonschema={surveyActivityJsonSchema}
              uischema={surveyActivityUiSchema(formData)}
              data={formData}
              onChange={handleJsonChange}
            />
          </div>
        </div>
      </div>
      <div className='flex flex-col gap-4'>
        <h3 className='text-base font-bold text-white'>Availability</h3>
        <div className={styles.availabilityRowWrap}>
          <JsonFormsWrapper
            jsonschema={surveyAvailabilityJsonSchema}
            uischema={availabilityUi}
            data={formData}
            onChange={handleJsonChange}
          />
        </div>
        <div className={styles.displayFirstAndOrderStack}>
          <div className={styles.displayFirstRow}>
            <div className={styles.displayFirstToggleWrap}>
              <Toggle
                id='survey_display_first'
                value={displayFirstOn}
                updateValue={onDisplayFirstUpdate}
                uiSchema={displayFirstToggleUiSchema}
              />
            </div>
          </div>
          <div className={styles.surveyFieldBlock}>
            <span className={segmentFieldLabelClass}>
              Display Order{displayFirstOn ? '' : '*'}
            </span>
            <div className={styles.surveyFieldDropdownWrap}>
              <Dropdown
                isJsonWrap={false}
                isDark
                label=''
                placeholder='Select'
                childList={DISPLAY_ORDER_OPTIONS}
                value={displayOrderDropdownValue}
                updateValue={onDisplayOrderSelect}
                disabled={displayFirstOn}
                isClearable={false}
                preventTyping={true}
                className={styles.surveyDisplayOrderDropdown}
              />
            </div>
            {displayOrderMissing ? (
              <p
                className={styles.displayOrderError}
                data-testid='survey-display-order-error'
                role='alert'>
                {DISPLAY_ORDER_REQUIRED_MSG}
              </p>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

SurveyActivitySetUp.propTypes = {
  activitySelection: PropTypes.node,
  formData: PropTypes.object.isRequired,
  handleFormChange: PropTypes.func.isRequired
};

export default React.memo(SurveyActivitySetUp);
